"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-10">
        <div className="h-4 w-14 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-14 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-14 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-14 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8 lg:gap-10">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className="relative bg-transparent hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent px-0 py-0 h-auto">
              <Link to={item.href || "#"} className="text-foreground font-default text-[0.8125rem] font-medium uppercase tracking-[0.06em] py-2 relative group whitespace-nowrap">
                <span>{item.label}</span>
                <span className="pointer-events-none absolute left-0 -bottom-0.5 h-[1px] w-full origin-left scale-x-0 bg-primary transition-transform duration-[260ms] ease-out group-hover:scale-x-100" aria-hidden="true" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4">
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-36 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onNavigate} className="text-foreground font-serif text-2xl font-normal tracking-tight py-3 border-b border-dotted border-border/30 hover:text-primary transition-colors duration-200">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1006" id="سربرگ-اصلی" className={`sticky top-0 z-50 w-full transition-all duration-[280ms] ease-out bg-background ${scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border" : "border-b border-transparent"}`}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      <div className="hidden lg:block absolute top-1/2 -translate-y-1/2 left-4 xl:left-6 text-[0.7rem] font-mono font-medium tracking-[0.18em] uppercase text-secondary select-none pointer-events-none">
        — Nº 01 · ARQ
      </div>

      <div className="relative mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 items-center h-[68px] md:h-[84px]">
          <div className="col-span-6 md:col-span-4 flex items-center">
            <Link to="/" className="inline-flex items-center" aria-label="ARQ — Home">
              <WvcLogo className="h-7 md:h-8 w-auto" />
            </Link>
          </div>

          <div className="col-span-6 md:col-span-8 flex items-center justify-end gap-6 lg:gap-10">
            <div className="hidden md:block">
              <MenuProvider menu_id="34" className="header-menu">
                <DesktopMenuConsumer />
              </MenuProvider>
            </div>

            <Button asChild className="hidden md:inline-flex relative overflow-hidden bg-transparent border border-primary text-foreground hover:bg-primary hover:text-primary-foreground font-mono font-medium uppercase tracking-[0.08em] text-[0.8125rem] h-auto px-7 py-4 transition-all duration-300 group">
              <Link to="/TalkWithUsPage">
                <span className="relative z-10">مشاوره</span>
              </Link>
            </Button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-transparent hover:text-primary" aria-label="Open menu">
                  <Menu className="size-6" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background border-l border-border w-[85%] sm:w-[380px] p-0 flex flex-col">
                <SheetTitle className="sr-only">منوی ناوبری</SheetTitle>

                <div className="px-8 pt-8 pb-6 border-b border-border">
                  <div className="text-[0.7rem] font-mono font-medium tracking-[0.18em] uppercase text-secondary mb-3">
                    — Nº 01 · ARQ
                  </div>
                  <Link to="/" onClick={() => setMobileOpen(false)} className="inline-flex items-center">
                    <WvcLogo className="h-7 w-auto" />
                  </Link>
                </div>

                <div className="flex-1 overflow-y-auto px-8 py-8">
                  <MenuProvider menu_id="34" className="header-menu-mobile">
                    <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="px-8 pb-10 pt-4 border-t border-border">
                  <Button asChild className="w-full relative overflow-hidden bg-transparent border border-primary text-foreground hover:bg-primary hover:text-primary-foreground font-mono font-medium uppercase tracking-[0.08em] text-[0.8125rem] h-auto px-7 py-4">
                    <Link to="/TalkWithUsPage" onClick={() => setMobileOpen(false)}>
                      <span className="relative z-10">شروع گفتگو</span>
                    </Link>
                  </Button>

                  <div className="mt-6 text-[0.7rem] font-mono tracking-[0.12em] uppercase text-secondary leading-relaxed">
                    شنبه–چهارشنبه · ۰۹:۰۰ — ۱۸:۰۰
                    <br />
                    (۰۲۱) ۱۲۳-۴۵۶۷
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}